import React from "react";
import styled from "styled-components";
import Breadcrumb from "sushiweb/Breadcrumb";
import Back from "sushiweb/Icons/all/Back";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { isMobile, isValidated, isValNull } from "../../utils";
import variables from "../../constants/assets/variables";
import Button from "sushiweb/Button";
import Input from "sushiweb/InputTypes/Input";
import Textarea from "sushiweb/InputTypes/Textarea";
import fieldsState from "./fieldState.json";
import {
  Divider,
  FieldsContainer,
  FieldsSection,
  FlexContainer,
} from "../../constants/assets/containers";
import PaginatedReactDropdown from "../../components/Common/PaginatedReactDropdown";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import * as IssueTypesActions from "../../store/actions/IssueTypes";
import * as UserActions from "../../store/actions/User";
import MenuActionsMappings from "../../constants/Menu/MenuActionsMappings";
import MenuNames from "../../constants/Menu/MenuNames.json";
import Permission from "../../components/Common/Permission";
import ToggleSwitch from "../../components/Common/ToggleSwitch";
import PlusCircle from "sushiweb/Icons/all/PlusCircle";
import CrossCircle from "sushiweb/Icons/all/CrossCircle";
import MultiSelectDropdown from "../../components/Common/MultiSelectDropdown";

const mandatoryFieldsToCheck = [
  "name",
  "externalMappingName",
  "issueArea",
  "visibility",
  "enabled",
];

const IssueTypesAction = (props) => {
  const {
    getIssueArea,
    getIssueTypeDetails,
    issueTypeDetails,
    saveUpdateIssueType,
    setStatus,
    activeFilters,
    getIssueTypeList,
    currentPage,
  } = props;
  const history = useHistory();
  const { id, action } = useParams();
  const actionType = !id ? "create" : action;
  const issueId = id;
  const [inputFieldState, setInputFieldState] = React.useState(fieldsState);
  const [conditionsList, setConditionsList] = React.useState([]);
  const textareaRef = React.useRef(null);
  const allActions = MenuActionsMappings[MenuNames.LOGISTICS_SUPPORT_DASHBOARD];
  const [isLoading, setIsLoading] = React.useState(false);
  const [issueAreaOptions, setIssueAreaOptions] = React.useState([]);
  const [visibilityOptions, setVisibilityOptions] = React.useState([]);
  const [conditionsConfig, setConditionsConfig] = React.useState([]);

  const languages = [
    { code: "en", label: "English (en)" },
    { code: "hi", label: "Hindi (hi)" },
    { code: "ta", label: "Tamil (ta)" },
    { code: "te", label: "Telugu (te)" },
    { code: "kn", label: "Kannada (kn)" },
    { code: "bn", label: "Bengali (bn)" },
  ];

  const booleanOptions = [
    { key: true, label: "true" },
    { key: false, label: "false" },
  ];

  const validateConditions = (conditions) => {
    let errors = [];
    if (!Array.isArray(conditions)) {
      errors.push("Conditions must be an array.");
      return errors;
    }

    conditions.forEach((item, index) => {
      if (typeof item !== "object" || item === null) {
        errors.push(`Condition at index ${index} must be an object.`);
      } else if (!item.hasOwnProperty("key") || !item.hasOwnProperty("value")) {
        errors.push(
          `Condition at index ${index} must have "key" and "value" properties.`
        );
      } else {
        const key = item.key;
        const value = item.value;

        if (!key || key.trim() === "") {
          errors.push(`Condition at index ${index} has an empty key.`);
          return;
        }

        const configItem = conditionsConfig.find(c => c.name === key);
        if (!configItem) {
          errors.push(`Invalid condition field: ${key}.`);
        } else {
          const dataType = configItem.data_type;

          if (dataType === "bool") {
            if (typeof value !== "boolean") {
              errors.push(
                `Value for key "${key}" at index ${index} must be a boolean (true or false without quotes).`
              );
            }
          } else if (dataType === "string") {
            if (typeof value !== "string") {
              errors.push(
                `Value for key "${key}" at index ${index} must be a string (with quotes).`
              );
            } else if (value.trim() === "") {
              errors.push(
                `Value for key "${key}" at index ${index} cannot be empty.`
              );
            }

            if (configItem.valid_values && configItem.valid_values.length > 0) {
              if (!configItem.valid_values.includes(value)) {
                errors.push(
                  `Value "${value}" for key "${key}" at index ${index} is not in the list of valid values.`
                );
              }
            }
          } else if (dataType === "[]string") {
            if (!Array.isArray(value)) {
              errors.push(
                `Value for key "${key}" at index ${index} must be an array.`
              );
            } else if (value.length === 0) {
              errors.push(
                `Value for key "${key}" at index ${index} cannot be an empty array.`
              );
            } else {
              value.forEach((el, i) => {
                if (typeof el !== "string") {
                  errors.push(
                    `Element at position ${i} for key "${key}" at index ${index} must be a string.`
                  );
                } else if (el.trim() === "") {
                  errors.push(
                    `Element at position ${i} for key "${key}" at index ${index} cannot be empty.`
                  );
                }

                if (configItem.valid_values && configItem.valid_values.length > 0) {
                  if (!configItem.valid_values.includes(el)) {
                    errors.push(
                      `Value "${el}" for key "${key}" at index ${index} is not in the list of valid values.`
                    );
                  }
                }
              });
            }
          }
        }
      }
    });

    return errors;
  };

  const transformConditionsToV2 = (conditions) => {
    const conditionsV2 = [];

    conditions.forEach((condition) => {
      const { key, value } = condition;

      if (!key || key.trim() === "") {
        return;
      }
      
      const configItem = conditionsConfig.find(c => c.name === key);
      if (!configItem) return;
      
      const dataType = configItem.data_type;
      
      if (
        (dataType === "string" && (value === "" || value.trim() === "")) ||
        (dataType === "[]string" &&
          Array.isArray(value) &&
          value.length === 0) ||
        value === undefined ||
        value === null
      ) {
        return;
      }

      const conditionObj = {};
      conditionObj[key] = value;
      conditionsV2.push(conditionObj);
    });

    return conditionsV2;
  };

  const handleAddCondition = () => {
    const hasEmptyCondition = conditionsList.some((condition) => {
      return !condition.key || condition.key.trim() === "";
    });

    if (hasEmptyCondition) {
      setStatus({
        type: "error",
        message: "Please select a condition key before adding a new condition.",
      });
      return;
    }

    const hasIncompleteCondition = conditionsList.some((condition) => {
      if (condition.key && condition.key.trim() !== "") {
        const configItem = conditionsConfig.find(c => c.name === condition.key);
        if (!configItem) return false;
        const dataType = configItem.data_type;

        if (dataType === "bool" && typeof condition.value !== "boolean") {
          return true;
        }

        if (
          dataType === "string" &&
          (condition.value === undefined ||
            condition.value === null ||
            condition.value.trim() === "")
        ) {
          return true;
        }

        if (
          dataType === "[]string" &&
          (!Array.isArray(condition.value) || condition.value.length === 0)
        ) {
          return true;
        }
      }

      return false;
    });

    if (hasIncompleteCondition) {
      setStatus({
        type: "error",
        message:
          "Please complete all current conditions before adding a new one.",
      });
      return;
    }

    setConditionsList([
      ...conditionsList,
      {
        key: "",
        value: "",
      },
    ]);
  };

  const handleRemoveCondition = (index) => {
    const updatedConditions = [...conditionsList];
    updatedConditions.splice(index, 1);
    setConditionsList(updatedConditions);

    setInputFieldState((prevState) => ({
      ...prevState,
      conditions: JSON.stringify(updatedConditions),
    }));
  };

  const handleConditionChange = (index, field, value) => {
    const updatedConditions = [...conditionsList];

    if (field === "key") {
      if (arrayInputState[index]) {
        setArrayInputState((prev) => {
          const newState = { ...prev };
          delete newState[index];
          return newState;
        });
      }

      const configItem = conditionsConfig.find(c => c.name === value);
      if (!configItem) return;
      
      const dataType = configItem.data_type;
      
      updatedConditions[index] = {
        key: value,
        value:
          dataType === "bool" ? false : dataType === "[]string" ? [] : "",
      };
    } else {
      const configItem = conditionsConfig.find(c => c.name === updatedConditions[index].key);
      if (!configItem) return;
      
      const dataType = configItem.data_type;

      if (dataType === "bool") {
        updatedConditions[index].value = value === true || value === "true";
      } else if (dataType === "[]string") {
        if (Array.isArray(value)) {
          updatedConditions[index].value = value;
        } else if (typeof value === "string" && value.includes(",")) {
          updatedConditions[index].value = value
            .split(",")
            .map((item) => item.trim())
            .filter((item) => item !== "");
        } else if (typeof value === "string") {
          updatedConditions[index].value = value.trim() ? [value.trim()] : [];
        }
      } else {
        updatedConditions[index].value = value;
      }
    }

    setConditionsList(updatedConditions);

    setInputFieldState((prevState) => ({
      ...prevState,
      conditions: JSON.stringify(updatedConditions),
    }));
  };

  React.useEffect(() => {
    if (!conditionsConfig.length || !issueAreaOptions.length || !visibilityOptions.length) {
      console.log("Fetching issue areas and condition configurations...");
      getIssueArea()
        .then(response => {
          if (response && response.IssueAreas) {
            const formattedIssueAreas = response.IssueAreas.map(area => ({
              name: area
            }));
            setIssueAreaOptions(formattedIssueAreas);
          }
          
          if (response && response.visibilityTags) {
            setVisibilityOptions(response.visibilityTags);
          }
          
          if (response && response.conditions) {
            setConditionsConfig(response.conditions);
          }
        })
        .catch((err) => {
          console.error("Failed to fetch issue areas and configurations:", err);
          setStatus({
            type: "error",
            message: "Failed to load issue configurations. Please refresh the page.",
          });
        });
    }

    if (actionType !== "create" && issueId) {
      setIsLoading(true);
      getIssueTypeDetails(issueId)
        .catch((err) => {
          console.error("Failed to fetch issue details:", err);
          setStatus({
            type: "error",
            message: "Failed to load issue details. Please try again.",
          });
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [issueId, actionType, getIssueArea, getIssueTypeDetails, setStatus]);

  React.useEffect(() => {
    if (actionType !== "create") {
      const details = { ...issueTypeDetails };

      if (details.conditionsV2 && details.conditionsV2.length > 0) {
        try {
          const formattedConditions = details.conditionsV2.map((condition) => {
            if (condition.key && condition.hasOwnProperty("value")) {
              return condition;
            } else {
              const key = Object.keys(condition)[0];
              return {
                key: key,
                value: condition[key],
              };
            }
          });

          details.conditions = JSON.stringify(formattedConditions);

          setConditionsList(formattedConditions);
        } catch (err) {
          console.error("Error formatting conditionsV2:", err);
          details.conditions = "";
          setConditionsList([]);
        }
      } else {
        details.conditions = "";
        setConditionsList([]);
      }

      setInputFieldState(details);
    }
  }, [issueTypeDetails, actionType]);

  React.useEffect(() => {
    if (
      inputFieldState.conditions &&
      typeof inputFieldState.conditions === "string"
    ) {
      try {
        const parsedConditions = JSON.parse(inputFieldState.conditions);
        if (
          Array.isArray(parsedConditions) &&
          JSON.stringify(parsedConditions) !== JSON.stringify(conditionsList)
        ) {
          setConditionsList(parsedConditions);
        }
      } catch (e) {}
    }
  }, [inputFieldState.conditions]);

  const handleBackClick = () => {
    if (actionType === "create" || actionType === "view") {
      history.push(`/issue-types`);
    } else {
      history.push(`/issue-types/${issueId}/view`);
    }
  };

  const handleChange = (key, val) => {
    if (key === "enabled") {
      setInputFieldState((prevState) => ({
        ...prevState,
        [key]: !prevState.enabled,
      }));
    } else if (key === "conditions") {
      return;
    } else {
      setInputFieldState((prevState) => ({ ...prevState, [key]: val }));
    }
  };

  const handleTranslationChange = (language, e) => {
    const value = e?.target?.value !== undefined ? e.target.value : e;
    setInputFieldState((prevState) => {
      const currentTranslations = prevState.vernacularTranslations || {};
      return {
        ...prevState,
        vernacularTranslations: {
          ...currentTranslations,
          [language]: value,
        },
      };
    });
  };

  const checkDisabledField = () => {
    return actionType === "view";
  };

  const handleButtonClick = (action) => {
    if (action === "edit") {
      history.push(`/issue-types/${issueId}/edit`);
    } else if (action === "cancel") {
      history.goBack();
    } else if (action === "save") {
      const hasIncompleteCondition = conditionsList.some(
        (condition) =>
          condition.key &&
          (condition.value === "" ||
            (Array.isArray(condition.value) && condition.value.length === 0))
      );

      if (hasIncompleteCondition) {
        setStatus({
          type: "error",
          message:
            "All conditions must have non-empty values. Please complete or remove incomplete conditions.",
        });
        return;
      }
      const missingTranslations = [];
      languages.forEach((lang) => {
        const translation = inputFieldState.vernacularTranslations?.[lang.code];
        if (!translation || translation === "" || translation === "nil") {
          missingTranslations.push(lang.label);
        }
      });
      if (missingTranslations.length > 0) {
        setStatus({
          type: "error",
          message: `Please fill all translations: ${missingTranslations.join(
            ", "
          )}`,
        });
        return;
      }
      inputFieldState.rank = Number(inputFieldState.rank);
      let validatedState = isValidated(inputFieldState, mandatoryFieldsToCheck);
      if (validatedState === "validated") {
        const conditionsObj = conditionsList.length > 0 ? conditionsList : [];

        const condErrors = validateConditions(conditionsObj);
        if (condErrors.length > 0) {
          setStatus({
            type: "error",
            message: "Conditions validation failed: " + condErrors.join(" "),
          });
          return;
        }

        const payload = { ...inputFieldState };

        payload.conditionsV2 = transformConditionsToV2(conditionsObj);

        setIsLoading(true);

        try {
          saveUpdateIssueType(actionType, payload)
            .then((res) => {
              setTimeout(() => {
                if (actionType === "create") {
                  history.push(`/issue-types`);
                } else {
                  history.push(`/issue-types/${issueId}/view`);
                }
                getIssueTypeList(currentPage, activeFilters);
                setIsLoading(false);
              }, 1000);
            })
            .catch((err) => {
              console.error("Error saving issue type:", err);
              setStatus({
                type: "error",
                message: "Failed to save issue type. Please try again.",
              });
              setIsLoading(false);
            });
        } catch (err) {
          console.error("Exception during save:", err);
          setStatus({
            type: "error",
            message: "Failed to save issue type. Please try again.",
          });
          setIsLoading(false);
        }
      } else {
        setStatus({ type: "error", message: validatedState });
      }
    }
  };

  const [arrayInputState, setArrayInputState] = React.useState({});

  const getConditionKeyOptions = () => {
    return conditionsConfig.map(condition => ({
      key: condition.name,
      label: `${condition.name} (${condition.data_type})`,
      dataType: condition.data_type,
      validValues: condition.valid_values || []
    }));
  };

  const renderValue = (condition, index) => {
    const configItem = conditionsConfig.find(c => c.name === condition.key);
    if (!configItem) return null;
    
    const dataType = configItem.data_type;
    const validValues = configItem.valid_values || [];

    if (dataType === "bool") {
      if (checkDisabledField()) {
        return (
          <Input
            value={condition.value === true ? "true" : "false"}
            data-testid={`conditionValueText-${index}`}
            id="sushi-input"
            disabled={true}
            label="Value"
            innerWidth="70%"
          />
        );
      }

      const selectedOption = booleanOptions.find(
        (option) => option.key === condition.value
      );

      return (
        <PaginatedReactDropdown
          options={booleanOptions}
          data-testid={`conditionValueDropdown-${index}`}
          labelKey="label"
          valueKey="key"
          disabled={false}
          value={selectedOption?.label || ""}
          handleChange={(key, val) =>
            handleConditionChange(index, "value", val)
          }
          keyToChange="value"
          placeholder="Value"
          showCross={false}
          id={`conditionValueDropdown-${index}`}
          width="50%"
          labelWidth="auto"
        />
      );
    } else if (dataType === "[]string") {
      if (checkDisabledField()) {
        return (
          <Input
            value={Array.isArray(condition.value) ? condition.value.join(", ") : ""}
            data-testid={`conditionValueText-${index}`}
            id="sushi-input"
            disabled={true}
            label="Value"
            innerWidth="70%"
          />
        );
      }

      if (validValues && validValues.length > 0) {
        const options = validValues.map(val => ({
          key: val,
          label: val
        }));

        const selectedValues = Array.isArray(condition.value)
          ? condition.value.map(val => ({
              key: val,
              label: val
            }))
          : [];
            
        return (
          <MultiSelectDropdown
            options={options}
            data-testid={`conditionValueMultiSelect-${index}`}
            labelKey="label"
            valueKey="key"
            disabled={false}
            value={selectedValues}
            handleChange={(key, vals) => {
              const selectedVals = Array.isArray(vals) 
                ? vals.map(v => v.key || v)
                : [];
              handleConditionChange(index, "value", selectedVals);
            }}
            keyToChange="value"
            placeholder="Select values"
            showCross={true}
            id={`conditionValueMultiSelect-${index}`}
            width="70%"
            labelWidth="auto"
            isMulti={true}
          />
        );
      } else {
        const inputValue =
          arrayInputState[index] !== undefined
            ? arrayInputState[index]
            : Array.isArray(condition.value)
            ? condition.value.join(", ")
            : "";

        return (
          <Input
            value={inputValue}
            data-testid={`conditionValueInput-${index}`}
            id="sushi-input"
            onChange={(e) => {
              setArrayInputState((prev) => ({ ...prev, [index]: e }));

              if (e.includes(",")) {
                const arrayValue = e
                  .split(",")
                  .map((item) => item.trim())
                  .filter((item) => item !== "");

                handleConditionChange(index, "value", arrayValue);
              } else {
                handleConditionChange(
                  index,
                  "value",
                  [e.trim()].filter((item) => item !== "")
                );
              }
            }}
            disabled={checkDisabledField()}
            label="Value (comma-separated)"
            innerWidth="70%"
            placeholder="Enter values separated by commas"
          />
        );
      }
    } else if (dataType === "string") {
      if (checkDisabledField()) {
        return (
          <Input
            value={condition.value}
            data-testid={`conditionValueText-${index}`}
            id="sushi-input"
            disabled={true}
            label="Value"
            innerWidth="70%"
          />
        );
      }
      
      if (validValues && validValues.length > 0) {
        const options = validValues.map(val => ({
          key: val,
          label: val
        }));
        
        return (
          <PaginatedReactDropdown
            options={options}
            data-testid={`conditionValueDropdown-${index}`}
            labelKey="label"
            valueKey="key"
            disabled={false}
            value={condition.value}
            handleChange={(key, val) =>
              handleConditionChange(index, "value", val)
            }
            keyToChange="value"
            placeholder="Select value"
            showCross={false}
            id={`conditionValueDropdown-${index}`}
            width="70%"
            labelWidth="auto"
          />
        );
      } else {
        return (
          <Input
            value={condition.value}
            data-testid={`conditionValueInput-${index}`}
            id="sushi-input"
            onChange={(e) => handleConditionChange(index, "value", e)}
            disabled={checkDisabledField()}
            label="Value"
            innerWidth="70%"
          />
        );
      }
    }
    
    return null;
  };

  const getFilteredConditionOptions = (currentIndex) => {
    const conditionOptions = getConditionKeyOptions();
    
    const usedKeys = conditionsList.reduce((keys, condition, index) => {
      if (
        index !== currentIndex &&
        condition.key &&
        condition.key.trim() !== ""
      ) {
        keys.push(condition.key);
      }
      return keys;
    }, []);

    return conditionOptions.filter(
      (option) => !usedKeys.includes(option.key)
    );
  };

  const BreadCrumbBar = () => {
    return (
      <>
        <Back
          onClick={handleBackClick}
          className="mr-10 backBtn"
          color={variables.grey400}
        />
        <Breadcrumb className="sushi-breadcrumbs">
          <Breadcrumb.Link onClick={handleBackClick}>
            Issue Types
          </Breadcrumb.Link>
          {actionType === "create" ? (
            <Breadcrumb.Link id="createIssueTypeText">
              create new issue types
            </Breadcrumb.Link>
          ) : (
            <>
              <Breadcrumb.Link>{issueId}</Breadcrumb.Link>
              <Breadcrumb.Link>{actionType}</Breadcrumb.Link>
            </>
          )}
        </Breadcrumb>
      </>
    );
  };

  const RightActionButtons = () => {
    return actionType === "view" ? (
      <>
        <Permission requiredPerms={[allActions.UPDATE]}>
          <Button
            data-testid="editIssueTypeBtn"
            onClick={() => handleButtonClick("edit")}
            className="sushi-btn"
            appearance="outline"
          >
            Edit
          </Button>
        </Permission>
      </>
    ) : (
      <>
        <Button
          data-testid="saveIssueTypeBtn"
          className="sushi-btn mr-20"
          onClick={() => handleButtonClick("save")}
        >
          Save
        </Button>
        <Button
          data-testid="cancelIssueTypeBtn"
          onClick={() => handleButtonClick("cancel")}
          className="sushi-btn"
          appearance="outline"
        >
          Cancel
        </Button>
      </>
    );
  };

  return (
    <RiderIssueTypeActionViewContainer>
      {isLoading ? (
        <div>Loading issue details...</div>
      ) : (
        <>
          <FlexContainer>
            <LeftContainer justify="flex-start">
              {BreadCrumbBar()}
            </LeftContainer>
            <RightContainer flexpct="25">{RightActionButtons()}</RightContainer>
          </FlexContainer>
          <FieldsContainer border noPadding className="mt-40">
            <FieldsHolder>
              <FieldsSection marginBottom="1.75rem">
                <p className="section-heading">BASIC DETAILS</p>
              </FieldsSection>
              <Input
                value={inputFieldState.code}
                data-testid="issueTypeFieldCode"
                id="sushi-input"
                onChange={(e) => handleChange("code", e)}
                disabled={checkDisabledField()}
                label="Code"
                innerWidth="20%"
              />
              <Input
                value={inputFieldState.name}
                data-testid="issueTypeFieldName"
                id="sushi-input"
                onChange={(e) => handleChange("name", e)}
                disabled={checkDisabledField()}
                label="Name"
                innerWidth="50%"
              />
              <Input
                value={inputFieldState.externalMappingName}
                data-testid="issueTypeFieldExternalMappingName"
                id="sushi-input"
                onChange={(e) => handleChange("externalMappingName", e)}
                disabled={checkDisabledField()}
                label="External Mapping Name"
                innerWidth="70%"
              />
              <Input
                value={inputFieldState.rank}
                type="number"
                data-testid="issueTypeFieldRank"
                id="sushi-input"
                onChange={(e) => handleChange("rank", e)}
                disabled={checkDisabledField()}
                label="Rank"
                innerWidth="30%"
              />
              <ToggleSwitch
                className="text-muted"
                label="Enabled"
                id="sushi-switch"
                size={10}
                spacing={4}
                isActive={inputFieldState.enabled}
                handleChange={() => handleChange("enabled")}
                keyName={`enabled`}
                disabled={checkDisabledField()}
                changeOnState={true}
              />

              {/* CONDITIONS SECTION */}
              <FieldsSection marginBottom="1.75rem" marginTop="2rem">
                <p className="section-heading">CONDITIONS</p>
              </FieldsSection>

              {conditionsList.map((condition, index) => (
                <ConditionSection key={index}>
                  <p className="sectionHeading">Condition {index + 1}</p>
                  <FlexContainer justify="space-between">
                    <div style={{ width: "90%" }}>
                      <PaginatedReactDropdown
                        options={getFilteredConditionOptions(index)}
                        data-testid={`conditionKeyDropdown-${index}`}
                        labelKey="label"
                        valueKey="key"
                        labelWidth="auto"
                        disabled={checkDisabledField()}
                        value={condition.key}
                        handleChange={(key, val) =>
                          handleConditionChange(index, "key", val)
                        }
                        keyToChange="key"
                        placeholder="Select condition key"
                        showCross={false}
                        id={`conditionKeyDropdown-${index}`}
                        width="50%"
                      />

                      {condition.key && renderValue(condition, index)}
                    </div>

                    {!checkDisabledField() && (
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <CrossCircle
                          onClick={() => handleRemoveCondition(index)}
                          color={variables.grey400}
                          size={20}
                          style={{ cursor: "pointer" }}
                        />
                      </div>
                    )}
                  </FlexContainer>
                </ConditionSection>
              ))}

              {!checkDisabledField() && (
                <AddConditionContainer justify={"flex-end"}>
                  <div className="flex" onClick={handleAddCondition}>
                    <PlusCircle color={variables.red500} size={20} />
                    <span>
                      Add {conditionsList.length ? "another" : "a"} condition
                    </span>
                  </div>
                </AddConditionContainer>
              )}

              <HorizontalScrollTextarea>
                <Textarea
                  id="sushi-textarea"
                  className="sushi-textarea"
                  data-testid="issueTypeFieldConditions"
                  borderColor={variables.primaryColor}
                  innerWidth="80%"
                  resize="none"
                  ref={textareaRef}
                  rows={2}
                  dynamicHeight={false}
                  label="Conditions JSON"
                  onChange={(e) => handleChange("conditions", e)}
                  disabled={true}
                  value={inputFieldState.conditions}
                />
              </HorizontalScrollTextarea>
            </FieldsHolder>
            <Divider height="6px" color={variables.grey100} />
            <FieldsHolder>
              <FieldsSection marginBottom="1.75rem">
                <p className="section-heading">ENTITY TAG</p>
              </FieldsSection>
              <PaginatedReactDropdown
                options={issueAreaOptions}
                handleChange={handleChange}
                keyToChange="issueArea"
                placeholder="Issue Area"
                labelKey="name"
                valueKey="name"
                disabled={checkDisabledField()}
                showCross={true}
                id="issueTypeField"
                width="50%"
                labelWidth="auto"
                value={inputFieldState.issueArea}
              />
              <PaginatedReactDropdown
                options={visibilityOptions}
                handleChange={handleChange}
                keyToChange="visibility"
                placeholder="Visibility"
                disabled={checkDisabledField()}
                showCross={true}
                id="issueTypeField"
                width="50%"
                labelWidth="auto"
                value={inputFieldState.visibility}
              />
            </FieldsHolder>
            <FieldsHolder>
              <FieldsSection marginBottom="1.75rem">
                <p className="section-heading">Translations</p>
              </FieldsSection>
              {languages.map((lang) => (
                <Input
                  key={lang.code}
                  value={
                    checkDisabledField() &&
                    !inputFieldState.vernacularTranslations?.[lang.code]
                      ? "nil"
                      : inputFieldState.vernacularTranslations?.[lang.code] ||
                        ""
                  }
                  data-testid={`translation${
                    lang.code.charAt(0).toUpperCase() + lang.code.slice(1)
                  }`}
                  id="sushi-input"
                  disabled={checkDisabledField()}
                  onChange={(e) => handleTranslationChange(lang.code, e)}
                  label={lang.label}
                  innerWidth="70%"
                />
              ))}
            </FieldsHolder>
          </FieldsContainer>
        </>
      )}
    </RiderIssueTypeActionViewContainer>
  );
};

const RiderIssueTypeActionViewContainer = styled.section``;
const LeftContainer = styled(FlexContainer)`
  flex: 0 1 30%;
  margin-bottom: ${isMobile() ? "20px" : "unset"};
  & > div {
    flex-direction: ${isMobile() ? "row" : "column"};
  }
`;
const RightContainer = styled(FlexContainer)`
  flex: ${({ flexpct }) => (flexpct ? `0 1 ${flexpct}%` : "0 1 35%")};
  justify-content: flex-end;
  .actionDropdown {
    width: 100%;
    margin-bottom: unset;
  }
`;
const FieldsHolder = styled.div`
  padding: 1.875rem;
`;
const HorizontalScrollTextarea = styled.div`
  .sushi-textarea {
    white-space: nowrap;
    overflow-x: auto !important;
    overflow-y: hidden;
  }
  textarea {
    white-space: nowrap;
    font-family: monospace;
  }
`;
const ConditionSection = styled.div`
  .sectionHeading {
    letter-spacing: 0.18rem;
    color: ${variables.grey600};
    margin-bottom: 1rem;
    margin-top: 1rem;
    font-size: 12px;
  }
  border-top: 1px solid ${variables.grey200};
  margin-bottom: 20px;
  padding-top: 10px;
`;

const AddConditionContainer = styled(FlexContainer)`
  span {
    color: ${variables.red500};
    font-weight: 500;
    margin-left: 5px;
  }
  margin: 0 1.5rem 1.5rem 0;
  cursor: pointer;
`;

const mapStateToProps = (state) => {
  const { IssueTypes } = state;
  return {
    issueArea: IssueTypes.issueArea,
    issueTypeDetails: IssueTypes.issueTypeDetails,
    activeFilters: IssueTypes.activeFilters,
    currentPage: IssueTypes.currentPage,
  };
};
const mapDispatchToProps = (dispatch) => {
  return {
    getIssueArea: bindActionCreators(IssueTypesActions.getIssueArea, dispatch),
    getIssueTypeDetails: bindActionCreators(
      IssueTypesActions.getIssueTypeDetails,
      dispatch
    ),
    saveUpdateIssueType: bindActionCreators(
      IssueTypesActions.saveUpdateIssueType,
      dispatch
    ),
    getIssueTypeList: bindActionCreators(
      IssueTypesActions.getIssueTypeList,
      dispatch
    ),
    setStatus: bindActionCreators(UserActions.setStatus, dispatch),
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(IssueTypesAction);